import React, { useState } from 'react';
import { saveData } from 'services/api/saveData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { IntegrationPageActionsProps } from '../IntegrationPage';
import OrderIntegrationButton from '../OrderIntegrationButton';
import DeactivateIntegrationButton from '../DeactivateIntegrationButton';
import { useOrderableModuleFeatures } from '../../billing/functions/useOrderableModuleFeatures';
import { useModal } from 'components/modals/ModalContext';
import { useTranslation } from 'react-i18next';

const ExactIntegrationPageActions: React.FC<IntegrationPageActionsProps> = ({
    response,
    form,
    integrationSlug,
    integrationId,
    featureKey,
    handleRefetch
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { allowedRights, environmentType } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const [resettingImport, setResettingImport] = useState(false);

    // Get the form component
    const FormComponent = form;

    // Determine whether the paid integration feature is active
    const moduleFeatures = useOrderableModuleFeatures();
    const isFeatureActive = featureKey ? environmentType === 'trial' || !!moduleFeatures?.availableFeatures.includes(featureKey) : true;

    // Reset the import date of the integration, forcing a full re-sync
    const handleResetImport = async () => {
        setResettingImport(true);

        try {
            await saveData({
                apiUrl: `reset_integration_import/${integrationSlug}/${integrationId}`,
                method: 'post',
                data: {}
            });

            setFloatingAlert({
                type: 'success',
                message: t('integrations.general.reset_import_integration_success')
            });

            handleRefetch?.();
        } catch (error) {
            console.error('Error resetting import', error);
            setFloatingAlert({
                type: 'danger',
                message: t('integrations.general.reset_import_integration_error')
            });
        } finally {
            setResettingImport(false);
        }
    };

    // Open the integration form
    const openIntegrationForm = async (integrationId?: number) => {
        if (!form) return;
        initializeModal(
            React.cloneElement(<FormComponent />), {
                title: t('integrations.general.configure_integration_title'),
                itemId: integrationId,
                modalSize: 'small-medium',
                onSuccess: (data: any) => {
                    if (data?.redirect_to) {
                        window.location.assign(data.redirect_to);
                    }
                }
            }
        );
    };

    // Wait for the module features before showing the actions
    if (featureKey && environmentType !== 'trial' && !moduleFeatures) {
        return null;
    }

    return (
        <div className="integration-actions">
            {featureKey && !isFeatureActive ? (
                <OrderIntegrationButton
                    featureKey={featureKey}
                    onOrdered={handleRefetch}
                />
            ) : !response?.is_connected ? (
                <PrimaryButton
                    label="integrations.general.activate_integration_label"
                    onClick={() => openIntegrationForm()}
                />
            ) : (
                <>
                    <SecondaryButton
                        label="integrations.general.edit_integration_label"
                        onClick={() => openIntegrationForm(integrationId)}
                    />
                    {allowedRights.includes('no_user') && integrationSlug && (
                        <SecondaryButton
                            label="integrations.general.reset_import_integration_label"
                            loading={resettingImport}
                            onClick={handleResetImport}
                        />
                    )}
                    {response?.should_reconnect && (
                        <PrimaryButton
                            label="integrations.general.reconnect_integration_label"
                            onClick={() => openIntegrationForm(integrationId)}
                        />
                    )}
                    {featureKey && environmentType !== 'trial' && (
                        <DeactivateIntegrationButton
                            featureKey={featureKey}
                            onDeactivated={handleRefetch}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ExactIntegrationPageActions;